/* eslint-disable react/jsx-pascal-case */
import React, {useState, useEffect, useRef} from 'react';
import cx from 'classnames';
import {useRouter} from 'next/router';
import {useTranslations, createTranslator} from 'next-intl';
import {Grid, Row, Col} from '@/grid';
import withMainLayout from 'layouts/main';
import {SectionPaddingMdToZero} from '@/section';
import Container from '@/container';
import Link from '@/links';
import {H2, H3, P, PMarginSm} from '@/typography';
import {ListNumbered, ListDisc} from '@/ui/lists/list-icons';
import {withMarginBottomMd, withMarginBottomXl, withMarginBottomXxl} from '@/hocs/withSpacing';
import {withBoldWeight} from '@/hocs/withFontType';
import withBreakpoints from '@/hocs/withBreakpoints';
import {withVisibleDesktop993, withHiddenDesktop993} from '@/hocs/withResponsiveUtils';
import TermsForm from '@/ui/hubspotform/terms/terms-form';
import Breadcrumbs from '@/ui/breadcrumbs';
import Dropdown from '@/form-fields/dropdown';
import Tabs from '@/ui/tabs/tabs';
import LegalHero from '@/content/legal/hero';
import {
  handlePrint,
  handleDropdown,
  scrollToTopOfPage,
  scrollableDropdownMob,
  setTabUrl
} from '@/ui/legal/legal.helper';
import {pageMessagesProps} from 'helpers/i18n';
import legal from '../legal.module.scss';
import shared from '../../../messages/en/shared.json';
import legalMessages from '../../../messages/en/legal.json';
import legalCompliance from '../../../messages/en/legalCompliance.json';

const seoMetas = props => {
  const seoT = createTranslator({locale: props.locale, messages: props.messages});
  return {
    title: seoT('legalCompliance.seo.title'),
    description: seoT('legalCompliance.seo.description'),
    canonical: seoT('legalCompliance.seo.canonical')
  };
};

const BoldSpan = withBoldWeight('span');
const MarginedH2 = withMarginBottomMd(H2);
const DivMarginedBottomXl = withMarginBottomXl('div');
const ListOl = props => <ListNumbered {...props} />;
const BreadcrumbsDesktop = withVisibleDesktop993(withMarginBottomXxl('div'));
const HiddenInDesktop = withHiddenDesktop993('div');

const CompliancePage = () => {
  const router = useRouter();
  const dropdownRef = useRef();
  const [activeTab, setActiveTab] = useState('dsa-notice');
  const [isDropdownFixed, setIsDropdownFixed] = useState(false);
  const t = useTranslations('legalCompliance');
  const legalT = useTranslations('legal');

  const tabItems = [
    {
      text: legalT('tabs.compliance.dsaNotice'),
      value: 'dsa-notice',
      child: true
    },
    {
      text: legalT('tabs.compliance.modernSlavery'),
      value: 'modern-slavery',
      child: false,
      routerPushUrl: '/modern-slavery-statement'
    },
    {
      text: legalT('tabs.compliance.webAccessibility'),
      value: 'web-accessibility',
      child: false,
      routerPushUrl: '/web-accessibility-statement'
    }
  ];

  useEffect(() => {
    setTabUrl(activeTab, setActiveTab, router);
  }, []);

  useEffect(() => {
    scrollableDropdownMob(dropdownRef, setIsDropdownFixed);
  }, []);

  const ResponsiveMenu = withBreakpoints({
    desktop: () => (
      <Tabs
        activeTab={activeTab}
        title={legalT('sectionTitles.compliance')}
        tabItems={tabItems}
        setActiveTab={setActiveTab}
        router={router}
      />
    ),
    mobile: () => (
      <div
        ref={dropdownRef}
        className={cx(
          isDropdownFixed ? legal['legal-dropdown-fixed'] : legal['legal-dropdown'],
          'no-print'
        )}>
        <Dropdown
          name="compliance"
          defaultValue={activeTab}
          variant="green800"
          values={tabItems}
          onChange={item => handleDropdown(item, router, setActiveTab)}
        />
      </div>
    )
  });

  const DsaNotice = () => (
    <>
      <MarginedH2>{t('dsaNotice.heading')}</MarginedH2>
      <DivMarginedBottomXl>
        <Link onClick={handlePrint} variant="secondary-no-gradient-download" className="no-print">
          {t('dsaNotice.downloadLabel')}
        </Link>
      </DivMarginedBottomXl>
      <P>{t('dsaNotice.intro')}</P>
      <ListOl>
        <li>
          <H3 className="heading-4">{t('dsaNotice.dsaDefinition.title')}</H3>
          <PMarginSm>
            {t.rich('dsaNotice.dsaDefinition.introRich', {
              bold: chunks => <BoldSpan>{chunks}</BoldSpan>
            })}
          </PMarginSm>
          {t.raw('dsaNotice.dsaDefinition.paragraphs').map((paragraph, index, arr) => {
            const ParagraphTag = index === arr.length - 1 ? P : PMarginSm;
            return <ParagraphTag key={paragraph}>{paragraph}</ParagraphTag>;
          })}
        </li>
        {t.raw('dsaNotice.sections').map(section => (
          <li key={section.title}>
            <H3 className="heading-4">{section.title}</H3>
            {section.paragraphs.map((paragraph, index, arr) => {
              const ParagraphTag = index === arr.length - 1 ? P : PMarginSm;
              return <ParagraphTag key={paragraph}>{paragraph}</ParagraphTag>;
            })}
          </li>
        ))}
        <li>
          <H3 className="heading-4">{t('dsaNotice.transparencyReports.title')}</H3>
          <P>{t('dsaNotice.transparencyReports.paragraph')}</P>
          <ListDisc>
            {t.raw('dsaNotice.transparencyReports.reports').map(report => (
              <li key={report.href}>
                <Link href={report.href} target="_blank" variant="default">
                  {report.text}
                </Link>
              </li>
            ))}
          </ListDisc>
        </li>
      </ListOl>
    </>
  );

  return (
    <>
      <LegalHero title={legalT('sectionTitles.compliance')} />
      <SectionPaddingMdToZero>
        <Container>
          <Grid fluid>
            <BreadcrumbsDesktop>
              <Breadcrumbs
                clickableLink={{text: legalT('breadcrumb.legal'), urlCopy: '/legal'}}
                title={legalT('sectionTitles.compliance')}
                activeTab={tabItems.find(item => item.value === activeTab).text}
              />
            </BreadcrumbsDesktop>
            <Row>
              <Col xs={12} md={12} lg={3}>
                <ResponsiveMenu />
              </Col>
              <Col xs={12} md={12} lgOffset={1} lg={8}>
                <DsaNotice />
              </Col>
            </Row>
            <TermsForm />
          </Grid>
          <HiddenInDesktop>
            <Link variant="go-to-top-arrow" onClick={scrollToTopOfPage} />
          </HiddenInDesktop>
        </Container>
      </SectionPaddingMdToZero>
    </>
  );
};

export default withMainLayout(CompliancePage, seoMetas);

export const messagesForIntl = {shared, legal: legalMessages, legalCompliance};

export function getStaticProps() {
  return pageMessagesProps(messagesForIntl);
}
